import './PrimaryNav.scss';
import { useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { CwIcon } from '../../icons/CwIcon';
import { resolveNavIcon } from '../../../utilities/navIconMap';

function isPathActive(pathname, linkPath) {
    if (!pathname || !linkPath) return false;
    if (linkPath === '/') return pathname === '/';
    return pathname === linkPath || pathname.startsWith(linkPath + '/');
}

function NavItem({ link, pathname, isRail, expandedMap, onToggleExpand, onNavigate }) {
    const hasChildren = Array.isArray(link.links) && link.links.length > 0;
    const anyChildActive = hasChildren
        ? link.links.some((child) => isPathActive(pathname, child.path))
        : false;
    const isActive = isPathActive(pathname, link.path) || anyChildActive;
    const isExpanded = expandedMap[link.key] === true
        || (expandedMap[link.key] === undefined && (link.isExpanded || anyChildActive));

    const iconName = resolveNavIcon(link.icon);

    const handleClick = useCallback(
        (ev) => {
            ev.preventDefault();
            if (hasChildren && !isRail) {
                onToggleExpand(link.key);
            }
            onNavigate(link);
        },
        [hasChildren, isRail, link, onToggleExpand, onNavigate]
    );

    return (
        <li className="cw-PrimaryNav__item-wrap">
            <a
                href={link.path}
                className="cw-PrimaryNav__item"
                aria-current={isActive ? 'page' : undefined}
                aria-expanded={hasChildren && !isRail ? isExpanded : undefined}
                data-tip={link.name}
                onClick={handleClick}
            >
                <CwIcon name={iconName} size={18} className="cw-PrimaryNav__icon" />
                <span className="cw-PrimaryNav__label">{link.name}</span>
                {link.badge != null ? (
                    <span className="cw-PrimaryNav__badge">{link.badge}</span>
                ) : null}
                {hasChildren ? (
                    <CwIcon
                        name="chevron"
                        size={14}
                        className="cw-PrimaryNav__chev"
                    />
                ) : null}
            </a>
            {hasChildren && isExpanded && !isRail ? (
                <ul className="cw-PrimaryNav__children">
                    {link.links.map((child) => (
                        <li key={child.key}>
                            <NavLink
                                to={child.path}
                                className={({ isActive: routeActive }) =>
                                    [
                                        'cw-PrimaryNav__item',
                                        'cw-PrimaryNav__item--child',
                                        routeActive ? 'cw-PrimaryNav__item--active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')
                                }
                                end={child.path === link.path}
                            >
                                <span className="cw-PrimaryNav__label">{child.name}</span>
                                {child.badge != null ? (
                                    <span className="cw-PrimaryNav__badge">{child.badge}</span>
                                ) : null}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

/**
 * Primary navigation for the Admin app.
 * Controlled component — rail state + toggle handler come from the parent.
 *
 * @param {object} props
 * @param {object[]} props.navGroups - nav groups from getPrimaryNavGroups()
 * @param {boolean} props.isRail - whether to render in 64px rail mode
 * @param {() => void} props.onToggleRail - toggles expanded ↔ rail
 */
export const PrimaryNav = ({ navGroups, isRail, onToggleRail }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [expandedMap, setExpandedMap] = useState({});

    const onToggleExpand = useCallback((key) => {
        setExpandedMap((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const onNavigate = useCallback(
        (link) => {
            if (link.path) navigate({ pathname: link.path });
        },
        [navigate]
    );

    return (
        <nav
            className={`cw-PrimaryNav ${isRail ? 'cw-PrimaryNav--rail' : ''}`}
            aria-label="Primary"
        >
            <div className="cw-PrimaryNav__head">
                <button
                    type="button"
                    className="cw-PrimaryNav__collapse"
                    onClick={onToggleRail}
                    aria-label={isRail ? 'Expand navigation' : 'Collapse navigation'}
                    aria-pressed={isRail}
                >
                    <CwIcon
                        name={isRail ? 'chevron-right' : 'chevron-left'}
                        size={16}
                        strokeWidth={1.8}
                    />
                </button>
            </div>

            {navGroups?.map((group, groupIdx) => (
                <div key={group.name ?? `group-${groupIdx}`} className="cw-PrimaryNav__group">
                    {group.name && !isRail ? (
                        <p className="cw-PrimaryNav__group-label">{group.name}</p>
                    ) : null}
                    <ul className="cw-PrimaryNav__list">
                        {group.links?.map((link) => (
                            <NavItem
                                key={link.key}
                                link={link}
                                pathname={location.pathname}
                                isRail={isRail}
                                expandedMap={expandedMap}
                                onToggleExpand={onToggleExpand}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </ul>
                </div>
            ))}
        </nav>
    );
};
